import { formatToSqlDateTime } from '@apply/ui';
import { LoanType } from '../../../../../../../../../common/constants/LoanType';
import { LoanStatusType } from '../../../../../../../../../common/constants/LoanStatusType';
import { findMatchingTermOptionKey } from '../../../../../../../../../common/functions/getLoanTermOptions';
import { StandardLoanDetails } from '../../../../../../queries/standardLoanQueries';
import { SetupForm, SetupLoanContext } from '../constants/setupForm';

/** Sub notes always have a fixed 60-month term (EditStdLoanModal's '60-0' option). */
const SUB_NOTE_TERM_MONTHS = 60;
const SUB_NOTE_TERM_KEY = '60-0';

/**
 * The Setup section's form, Term choice and context for a loaded loan, following EditStdLoanModal's populateState.
 * loanTypeId is the loan's, or the one the modal was opened with when a new-loan template doesn't say.
 */
export const getSetupFromLoan = (loan: StandardLoanDetails, loanTypeId: LoanType | undefined) => {
    const isSubNote = loanTypeId === LoanType.SubNoteConstruction || loanTypeId === LoanType.SubNoteFixFlip;

    // The endpoints' dates, as the 'YYYY-MM-DD HH:mm:ss' strings DateTimeBox uses (see parseSetupDate)
    const toSetupDate = (value: string | null | undefined) => (value ? formatToSqlDateTime(new Date(value)) : undefined);

    const setup: SetupForm = {
        loanTypeId,
        initialTermMonths: isSubNote ? SUB_NOTE_TERM_MONTHS : (loan.initialTermMonths ?? undefined),
        contractualExtension1Months: isSubNote ? undefined : (loan.contractualExtension1Months ?? undefined),
        contractualExtension2Months: isSubNote ? undefined : (loan.contractualExtension2Months ?? undefined),
        approvalDate: toSetupDate(loan.approvalDate),
        noteDate: toSetupDate(loan.noteDate),
        maturityDate: toSetupDate(loan.maturityDate),
        bfiNumber: loan.bfiNumber ?? '',
        loanNumber: loan.loanNumber != null ? String(loan.loanNumber) : '',
        pointOfContactId: loan.pointOfContactId ?? '',
        underwriterId: loan.underwriterId ?? '',
        callCodeType: loan.callCodeType ?? undefined,
        inForbearance: loan.inForbearance ?? false,
        forbearanceAgreementTerms: loan.forbearanceAgreementTerms ?? '',
    };

    // The Term option matching the loan's term months: a preset, Custom, or '' when no term is set
    let selectedTermKey = '';
    if (isSubNote) {
        selectedTermKey = SUB_NOTE_TERM_KEY;
    } else if (loanTypeId !== undefined) {
        selectedTermKey = findMatchingTermOptionKey(
            loanTypeId,
            loan.initialTermMonths ?? null,
            loan.contractualExtension1Months ?? null,
            loan.contractualExtension2Months ?? null
        );
    }

    const setupLoanContext: SetupLoanContext = {
        existingLoanId: loan.id || undefined,
        statusId: loan.statusId ?? LoanStatusType.Approved,
        isSubNote,
        masterNoteBfiNumber: loan.masterNoteBfiNumber ?? undefined,
    };

    return { setup, selectedTermKey, setupLoanContext };
};
